import React, { JSX } from 'react';
import { Card, CardBody, Table, TableContainer, Tbody, Tfoot, Th, Thead, Tr } from '@chakra-ui/react';

import { useTranslations } from '../../../contexts/LocalizationContext';
import { WeeklyStatisticsResponse } from '../model/WeeklyStatisticsResponse';
import { calculateTotals, calcWoWChange, formatNumber, METRICS, ThresholdMap } from '../utils/metrics';
import MetricValueCell from './MetricValueCell';
import WeekCell from './WeekCell';

interface Props {
  weeks: WeeklyStatisticsResponse[];
  thresholdMap: ThresholdMap;
}

const MetricsTable = ({ weeks, thresholdMap }: Props): JSX.Element => {
  const translations = useTranslations();
  // Newest first
  const rows = [...weeks].reverse();
  const totals = calculateTotals(weeks);

  return (
    <Card borderRadius="xl">
      <CardBody>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{translations('monitoring_table_week')}</Th>
                {METRICS.map((m) => (
                  <Th key={m.key} textAlign="center">
                    {translations(m.translationKey)}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((w, index) => (
                <Tr key={`${w.year}-${w.weekNumber}`}>
                  <WeekCell
                    weekNumber={w.weekNumber}
                    weekStart={w.weekStart}
                    weekEnd={w.weekEnd}
                    isLatest={index === 0}
                  />
                  {METRICS.map((m) => {
                    const prevWeek = rows[index + 1];
                    const change = prevWeek ? calcWoWChange(w[m.key], prevWeek[m.key]) : null;
                    return (
                      <MetricValueCell key={m.key} value={w[m.key]} threshold={thresholdMap[m.key]} change={change} />
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
            <Tfoot>
              <Tr>
                <Th fontWeight="bold">{translations('monitoring_table_total')}</Th>
                {METRICS.map((m) => (
                  <Th key={m.key} fontWeight="bold" textAlign="center">
                    {formatNumber(totals[m.key] ?? 0)}
                  </Th>
                ))}
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
};

export default MetricsTable;
